import { createPinia } from "pinia";
import { createApp } from "vue";
import * as Sentry from "@sentry/vue";
import Toast from "vue-toastification";
import "vue-toastification/dist/index.css";

import App from "./App.vue";
import "./firebase"; // initializes Firebase before any auth call
import i18n from "./i18n/index.js";
import router from "./router";
import vuetify from "./plugins/vuetify";
import { useAuthStore } from "./stores/authStore";

// ── デプロイ後の chunk 取得失敗を自動復旧 ──
// Vite はビルド毎に chunk のハッシュが変わるため、新デプロイ後に古いタブが
// 旧 URL の動的 import / CSS preload を取得しようとして失敗する
// (Sentry: "Failed to fetch dynamically imported module" / "Unable to preload CSS")。
// 一度だけ自動リロードして最新アセットを取得する。直近に既にリロード済みなら
// 「本当にアセットが存在しない」ケースとみなし、無限リロードを避ける。
window.addEventListener("vite:preloadError", () => {
  const KEY = "vite:preloadError:lastReload";
  const last = Number(sessionStorage.getItem(KEY) || 0);
  if (Date.now() - last < 10_000) return; // 直近10秒以内に再発ならループ防止で何もしない
  sessionStorage.setItem(KEY, String(Date.now()));
  window.location.reload();
});

const pinia = createPinia();
const app = createApp(App);

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? "production",
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: true,
      }),
      Sentry.httpClientIntegration({
        failedRequestStatusCodes: [[500, 599]],
        failedRequestTargets: [/qumowill\.jp/],
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    beforeSend(event) {
      if (event.message?.includes("postMessage sign-in failed")) return null;
      return event;
    },
  });
}

app.use(pinia);
app.use(vuetify);
app.use(i18n);
app.use(Toast, {
  position: "top-right",
  timeout: 8000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  draggable: true,
  draggablePercent: 0.6,
});

const authStore = useAuthStore();
authStore.initAuth().then(() => {
  app.use(router);
  app.mount("#app");
});
